'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KindBadge } from '../chrome';
import { MicroFilterChips } from '../FilterChips';
import type { RendererProps } from '../types';
import { parseVideoSource } from '../videoSource';
import { Cinema8Embed } from './embeds';

//
// ShortsRenderer — immersive single-short player (legacy parity).
//
// A 9:16 frame the video fills (object-fit: cover), title + description
// overlaid on a bottom scrim, a thin bottom progress bar driven by
// `timeupdate`, a centre ▶ overlay for tap-to-pause / replay, a mute
// toggle, and a bookmark on the right rail — the same player the legacy
// popup rendered, sized here for the fullscreen reel modal (the popup
// covers the whole screen; this frame centres inside it and goes
// full-bleed on phones).
//
// Source switch: a native <video> gets the full polished player;
// YouTube / Cinema8 embeds keep their own iframe controls (you can't
// tap-pause or drive a custom progress bar on a cross-origin iframe)
// and render inside the same framed, titled shell. Completion is
// reported from the native `ended` event only — embeds do not
// auto-report completion (legacy behaviour).
//

export function ShortsRenderer({
  item,
  onComplete,
  favorite,
  onToggleFavorite,
  onAdvance,
  reelDirection,
  onClose,
}: RendererProps) {
  const body = (item.body as { videoUrl?: string; url?: string; posterUrl?: string }) ?? {};
  const videoUrl = body.videoUrl ?? body.url;
  const posterUrl = body.posterUrl ?? item.coverImageUrl ?? undefined;
  const source = parseVideoSource(videoUrl);

  // Feed feel: the incoming short slides in from the side the viewer
  // scrolled towards (the popup keys the renderer per item, so this
  // mounts fresh and the animation runs once).
  const enter =
    reelDirection === 'next'
      ? ' ms-wrap--from-below'
      : reelDirection === 'prev'
        ? ' ms-wrap--from-above'
        : '';

  return (
    <div className={`ms-wrap${enter}`}>
      <style>{MS_STYLES}</style>
      <div className="ms-frame">
        {source.type === 'native' ? (
          <NativeShort
            url={source.url}
            posterUrl={posterUrl}
            onComplete={onComplete}
            onAdvance={onAdvance}
          />
        ) : (
          <EmbedShort
            source={source}
            posterUrl={posterUrl}
            onComplete={onComplete}
            onAdvance={onAdvance}
          />
        )}
      </div>
      {/* YouTube-Shorts style: the video is the video; title, description
          and actions live beside it (below it on a phone), never over it. */}
      <aside className="ms-side">
        <KindBadge kind="SHORTS" />
        {item.title && <h3 className="ms-title">{item.title}</h3>}
        {item.description && <p className="ms-desc">{item.description}</p>}
        <MicroFilterChips item={item} onClose={onClose} style={{ marginTop: 2 }} />
        {onToggleFavorite && (
          <div className="ms-rail">
            <SaveButton favorite={favorite ?? false} onToggle={onToggleFavorite} />
          </div>
        )}
      </aside>
    </div>
  );
}

// -----------------------------------------------------------------------------
// NativeShort — the full polished player (HTML5 <video>)
// -----------------------------------------------------------------------------

function NativeShort({
  url,
  posterUrl,
  onComplete,
  onAdvance,
}: {
  url: string;
  posterUrl: string | undefined;
  onComplete: RendererProps['onComplete'];
  onAdvance: RendererProps['onAdvance'];
}) {
  const { t } = useTranslation('site-builder-blocks');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [ended, setEnded] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const completedRef = useRef(false);
  // Latest callbacks without re-binding the media listeners — a pending
  // "advance after the end frame" timer must survive parent re-renders.
  const callbacksRef = useRef({ onComplete, onAdvance });
  callbacksRef.current = { onComplete, onAdvance };

  // Autoplay (muted) on mount. If the browser blocks it the centre ▶
  // overlay shows and a tap starts playback.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.play()
      .then(() => setPaused(false))
      .catch(() => setPaused(true));
  }, []);

  // Keep the DOM element's muted flag in sync — React's `muted` prop is
  // unreliable across remounts, so we set it imperatively too.
  useEffect(() => {
    const el = videoRef.current;
    if (el) el.muted = muted;
  }, [muted]);

  // Progress bar + one-shot completion, driven by the media element.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    function onTimeUpdate() {
      if (!el!.duration || !Number.isFinite(el!.duration)) return;
      setProgress(el!.currentTime / el!.duration);
    }
    function onEnded() {
      setEnded(true);
      setPaused(true);
      setProgress(1);
      if (!completedRef.current) {
        completedRef.current = true;
        callbacksRef.current.onComplete();
      }
      // Feed behaviour: the next short follows on its own, after a beat
      // so the end frame registers. Nothing next → stay on the replay.
      advanceTimer = window.setTimeout(() => callbacksRef.current.onAdvance?.(), 700);
    }
    let advanceTimer: number | undefined;
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('ended', onEnded);
    return () => {
      if (advanceTimer != null) window.clearTimeout(advanceTimer);
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    // Replay from the top if we're at/after the end.
    if (el.ended || (el.duration && el.currentTime >= el.duration - 0.05)) {
      el.currentTime = 0;
      setEnded(false);
      el.play()
        .then(() => setPaused(false))
        .catch(() => setPaused(true));
      return;
    }
    if (el.paused) {
      el.play()
        .then(() => setPaused(false))
        .catch(() => setPaused(true));
    } else {
      el.pause();
      setPaused(true);
    }
  }, []);

  return (
    <>
      <video
        ref={videoRef}
        src={url}
        poster={posterUrl}
        muted={muted}
        autoPlay
        playsInline
        onClick={togglePlay}
        className="ms-video"
      />

      {/* Centre play / replay indicator — shown while paused or ended. */}
      {(paused || ended) && (
        <button
          type="button"
          className="ms-play"
          onClick={togglePlay}
          aria-label={ended ? t('microShorts.replay') : t('microShorts.play')}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>
      )}

      <Scrims />

      {/* TOP — mute, top-right */}
      <div className="ms-top">
        <span />
        <button
          type="button"
          className="ms-icon-btn"
          onClick={() => setMuted((m) => !m)}
          aria-label={muted ? t('microShorts.unmute') : t('microShorts.mute')}
        >
          {muted ? <MutedIcon /> : <UnmutedIcon />}
        </button>
      </div>

      {/* BOTTOM — progress */}
      <div className="ms-progress">
        <div className="ms-progress-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// EmbedShort — YouTube / Cinema8 / unrecognised, framed + titled
// -----------------------------------------------------------------------------

function EmbedShort({
  source,
  posterUrl,
  onComplete,
  onAdvance,
}: {
  source: ReturnType<typeof parseVideoSource>;
  posterUrl: string | undefined;
  onComplete: RendererProps['onComplete'];
  onAdvance: RendererProps['onAdvance'];
}) {
  const { t } = useTranslation('site-builder-blocks');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const completedRef = useRef(false);
  const callbacksRef = useRef({ onComplete, onAdvance });
  callbacksRef.current = { onComplete, onAdvance };
  // Player state mirrored from the IFrame API so our own controls can
  // drive it — the iframe sits under a full shield (see below).
  const [playing, setPlaying] = useState(true);
  // Autoplay muted, same as NativeShort — Chrome blocks unmuted autoplay
  // outright (silently, with no error a cross-origin iframe would surface),
  // which otherwise leaves the embed sitting paused until a click that
  // itself can't reliably unblock it either.
  const [muted, setMuted] = useState(true);
  const command = useCallback((func: string, args: unknown[] = []) => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args }),
      '*',
    );
  }, []);

  // YouTube's IFrame API talks over postMessage once asked to
  // (`enablejsapi=1` + a "listening" hello, then an addEventListener
  // command). A Shorts embed LOOPS on its own, so "ended" (state 0) may
  // never fire — the playhead jumping back to 0 from near the end is
  // the reliable signal, read off the periodic infoDelivery messages.
  useEffect(() => {
    if (source.type !== 'youtube') return;
    const post = (msg: Record<string, unknown>) =>
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify(msg), '*');
    const subscribe = () => {
      post({ event: 'listening', id: 1 });
      post({ event: 'command', func: 'addEventListener', args: ['onStateChange'] });
    };
    let advanceTimer: number | undefined;
    let lastTime = 0;
    let lastDuration = 0;
    const finish = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      callbacksRef.current.onComplete();
      advanceTimer = window.setTimeout(() => callbacksRef.current.onAdvance?.(), 400);
    };
    const onMessage = (e: MessageEvent) => {
      // Only THIS iframe. The previous short's player keeps posting for a
      // moment after it unmounts, and its near-the-end playhead read as
      // "this one just finished" — which is how the reel skipped the
      // short in between right after every advance.
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (typeof e.data !== 'string' || !/youtube/.test(e.origin)) return;
      let data: { event?: string; info?: unknown };
      try {
        data = JSON.parse(e.data) as { event?: string; info?: unknown };
      } catch {
        return;
      }
      if (data.event === 'onReady') subscribe();
      if (data.event === 'onStateChange') {
        if (data.info === 0) finish();
        if (data.info === 1) setPlaying(true);
        if (data.info === 2) setPlaying(false);
      }
      if (data.event === 'infoDelivery' && data.info && typeof data.info === 'object') {
        const info = data.info as {
          currentTime?: number;
          duration?: number;
          playerState?: number;
          muted?: boolean;
        };
        if (typeof info.playerState === 'number') setPlaying(info.playerState === 1);
        if (typeof info.muted === 'boolean') setMuted(info.muted);
        const t = info.currentTime ?? 0;
        const d = info.duration ?? 0;
        // A loop wrap only counts against a playhead read of the SAME video
        // (same duration) — never against whatever was reported before.
        const sameVideo = d > 0 && lastDuration === d;
        if (d > 0 && (t >= d - 0.6 || (sameVideo && lastTime > d * 0.7 && t < 1))) finish();
        lastTime = t;
        lastDuration = d;
      }
    };
    window.addEventListener('message', onMessage);
    subscribe();
    const helloTimer = window.setInterval(subscribe, 1500);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(helloTimer);
      if (advanceTimer != null) window.clearTimeout(advanceTimer);
    };
  }, [source.type]);

  return (
    <>
      {source.type === 'youtube' ? (
        <iframe
          ref={iframeRef}
          className="ms-embed"
          // `origin` is how the IFrame API verifies a postMessage command
          // actually came from the domain hosting the player — YouTube's
          // docs call it required for reliable playVideo/pauseVideo control.
          // Without it, command delivery is undocumented/best-effort and can
          // silently no-op on some domains while working on others.
          src={`${source.embedUrl}?rel=0&modestbranding=1&autoplay=1&mute=1&playsinline=1&enablejsapi=1&controls=0${
            typeof window !== 'undefined'
              ? `&origin=${encodeURIComponent(window.location.origin)}`
              : ''
          }`}
          title={t('microEmbeds.video.title')}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      ) : source.type === 'cinema8' ? (
        <div className="ms-embed">
          {/* Embeds do not auto-report completion (legacy behaviour) —
              Cinema8 exposes no "ended" event to the parent frame. */}
          <Cinema8Embed
            mediaId={source.mediaId}
            raw={source.raw}
            posterUrl={posterUrl}
            onComplete={noop}
          />
        </div>
      ) : (
        <div className="ms-fallback">{t('microShorts.unplayable')}</div>
      )}

      {/* A cross-origin iframe eats wheel and swipe events; gestures that
          start on these transparent margins still reach the reel's window
          listeners, so the feed scrolls from the frame's edges. */}
      {source.type === 'youtube' ? (
        <>
          {/* Full shield over the iframe: a cross-origin frame swallows
              wheel and swipe, so nothing may reach it directly. Tap =
              play / pause through the IFrame API; the reel's window
              listeners get every wheel / swipe / key. Our own mute
              button replaces the player's (controls=0). */}
          <button
            type="button"
            className="ms-shield"
            onClick={() => command(playing ? 'pauseVideo' : 'playVideo')}
            aria-label={playing ? t('microShorts.pause') : t('microShorts.play')}
          />
          {!playing && (
            <div className="ms-play" aria-hidden>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          )}
          <div className="ms-top">
            <span />
            <button
              type="button"
              className="ms-icon-btn"
              onClick={() => {
                command(muted ? 'unMute' : 'mute');
                setMuted((m) => !m);
              }}
              aria-label={muted ? t('microShorts.unmute') : t('microShorts.mute')}
            >
              {muted ? <MutedIcon /> : <UnmutedIcon />}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="ms-wheel-zone ms-wheel-zone--left" aria-hidden />
          <div className="ms-wheel-zone ms-wheel-zone--right" aria-hidden />
          <div className="ms-wheel-zone ms-wheel-zone--top" aria-hidden />
          <div className="ms-wheel-zone ms-wheel-zone--mid" aria-hidden />
        </>
      )}
    </>
  );
}

function noop() {}

/**
 * Dark 9:16 placeholder shown while a reel swap fetches the next
 * short's body — same frame geometry as the player, so the stage
 * doesn't jump when the video mounts.
 */
export function ShortsLoadingFrame() {
  return (
    <div className="ms-wrap">
      <style>{MS_STYLES}</style>
      <div className="ms-frame">
        <div className="ms-spinner" aria-hidden />
      </div>
      {/* Reserves the same horizontal space the info panel occupies once
          the real item loads — without this, .ms-frame sits centred alone
          here (no sibling to push it over) and visibly jumps left the
          moment .ms-side mounts alongside it. */}
      <aside className="ms-side" aria-hidden />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Shared bits
// -----------------------------------------------------------------------------

function SaveButton({ favorite, onToggle }: { favorite: boolean; onToggle: () => void }) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <button
      type="button"
      className={`ms-rail-btn${favorite ? ' is-active' : ''}`}
      onClick={onToggle}
      aria-pressed={favorite}
      aria-label={favorite ? t('microChrome.favorite.remove') : t('microChrome.favorite.add')}
    >
      <span className="ms-rail-icon">{favorite ? <BookmarkFilledIcon /> : <BookmarkIcon />}</span>
      <span className="ms-rail-label">{t('microShorts.save')}</span>
    </button>
  );
}

// Top + bottom gradient scrims so overlaid controls/text stay legible
// over bright video frames.
function Scrims() {
  return (
    <>
      <div className="ms-scrim ms-scrim-top" aria-hidden />
      <div className="ms-scrim ms-scrim-bottom" aria-hidden />
    </>
  );
}

// -----------------------------------------------------------------------------
// Icons (YouTube-shorts geometry, same set the legacy feed used)
// -----------------------------------------------------------------------------

function MutedIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
    </svg>
  );
}
function UnmutedIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
    </svg>
  );
}
function BookmarkIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2zm0 15l-5-2.18L7 18V5h10v13z" />
    </svg>
  );
}
function BookmarkFilledIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z" />
    </svg>
  );
}

// -----------------------------------------------------------------------------
// Scoped styles (.ms-*)
// -----------------------------------------------------------------------------

const MS_STYLES = `
  .ms-wrap {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 20px;
  }
  .ms-wrap--from-below { animation: msSlideUp 380ms cubic-bezier(0.22, 1, 0.36, 1); }
  .ms-wrap--from-above { animation: msSlideDown 380ms cubic-bezier(0.22, 1, 0.36, 1); }
  @keyframes msSlideUp {
    from { opacity: 0; transform: translateY(56px); }
    to { opacity: 1; transform: none; }
  }
  @keyframes msSlideDown {
    from { opacity: 0; transform: translateY(-56px); }
    to { opacity: 1; transform: none; }
  }
  @media (prefers-reduced-motion: reduce) {
    .ms-wrap--from-below, .ms-wrap--from-above { animation: none; }
  }
  .ms-side {
    width: 320px;
    max-width: 40vw;
    color: #fff;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 8px;
  }
  .ms-side .ms-title { font-size: 1.15rem; text-shadow: none; }
  .ms-side .ms-desc {
    text-shadow: none;
    color: rgba(255,255,255,0.85);
    display: block;
    -webkit-line-clamp: unset;
    overflow: visible;
    margin: 0;
  }
  .ms-side .ms-rail { position: static; align-items: flex-start; margin-top: 8px; }
  @media (max-width: 900px) {
    .ms-wrap { flex-direction: column; align-items: center; gap: 12px; }
    .ms-frame { height: min(calc(100dvh - 190px), 900px); }
    .ms-side { width: min(100vw - 24px, 420px); max-width: none; max-height: 150px; overflow: auto; }
  }
  .ms-frame {
    position: relative;
    height: min(calc(100dvh - 48px), 900px);
    aspect-ratio: 9 / 16;
    width: auto;
    max-width: calc(100vw - 24px);
    background: #000;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0,0,0,0.45);
    animation: msFade 180ms ease;
  }
  /* Phones: full-bleed like the Instagram / Facebook reel view. The
     frame IS the screen there, so the top bar leaves room on the right
     for the reel's fixed close button. */
  @media (max-width: 640px) {
    .ms-frame {
      height: calc(100dvh - 170px);
      max-width: 100vw;
      border-radius: 0;
      box-shadow: none;
    }
    .ms-top {
      padding-right: 72px;
    }
  }
  .ms-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 36px;
    height: 36px;
    margin: -18px 0 0 -18px;
    border-radius: 50%;
    border: 3px solid rgba(255,255,255,0.2);
    border-top-color: rgba(255,255,255,0.85);
    animation: msSpin 0.8s linear infinite;
  }
  @keyframes msSpin { to { transform: rotate(360deg); } }

  .ms-video, .ms-embed {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
    object-fit: cover;
    background: #000;
  }
  .ms-video { cursor: pointer; }
  /* Wheel-catching overlays over an embed. A cross-origin iframe swallows
     wheel events, so these transparent panes cover everything except a
     central window (tap to play / pause) and the player's own control bar
     at the bottom — the reel scrolls from almost anywhere, the player
     stays clickable where it matters. */
  .ms-shield {
    position: absolute;
    inset: 0;
    z-index: 2;
    border: 0;
    padding: 0;
    background: transparent;
    cursor: pointer;
  }
  .ms-play { pointer-events: none; }
  .ms-wheel-zone { position: absolute; z-index: 2; }
  .ms-wheel-zone--left { top: 0; bottom: 64px; left: 0; width: 28%; }
  .ms-wheel-zone--right { top: 0; bottom: 64px; right: 0; width: 28%; }
  .ms-wheel-zone--top { top: 64px; height: 22%; left: 28%; right: 28%; }
  .ms-wheel-zone--mid { top: 58%; bottom: 64px; left: 28%; right: 28%; }

  .ms-fallback {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1.5rem;
    color: #94a3b8;
    font-size: 0.9rem;
  }

  /* Scrims */
  .ms-scrim {
    position: absolute;
    left: 0;
    right: 0;
    pointer-events: none;
    z-index: 1;
  }
  .ms-scrim-top {
    top: 0;
    height: 96px;
    background: linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0));
  }
  .ms-scrim-bottom {
    bottom: 0;
    height: 200px;
    background: linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0));
  }

  /* Centre play / replay */
  .ms-play {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: 0;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    color: #fff;
    cursor: pointer;
    display: grid;
    place-items: center;
    z-index: 4;
    animation: msFade 160ms ease;
  }
  .ms-play svg { width: 34px; height: 34px; }
  @keyframes msFade { from { opacity: 0; } to { opacity: 1; } }

  /* Top bar */
  .ms-top {
    position: absolute;
    top: 0; left: 0; right: 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 12px;
    z-index: 3;
  }
  .ms-icon-btn {
    appearance: none;
    border: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    color: #fff;
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: background 120ms ease;
  }
  .ms-icon-btn:hover { background: rgba(0,0,0,0.75); }
  .ms-icon-btn svg { width: 22px; height: 22px; fill: currentColor; }

  /* Left overlay */
  .ms-left {
    position: absolute;
    left: 14px;
    right: 76px;
    bottom: 18px;
    z-index: 3;
    color: #fff;
  }
  .ms-title {
    margin: 0 0 6px;
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.25;
    text-shadow: 0 1px 4px rgba(0,0,0,0.6);
  }
  .ms-desc {
    font-size: 0.85rem;
    line-height: 1.4;
    color: rgba(255,255,255,0.92);
    text-shadow: 0 1px 3px rgba(0,0,0,0.6);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .ms-desc.is-expanded {
    -webkit-line-clamp: unset;
    overflow: visible;
  }
  .ms-desc-toggle {
    appearance: none;
    border: 0;
    background: transparent;
    color: rgba(255,255,255,0.7);
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
  }

  /* Right rail (bookmark only) */
  .ms-rail {
    position: absolute;
    right: 12px;
    bottom: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    z-index: 3;
  }
  .ms-rail-btn {
    appearance: none;
    border: 0;
    background: transparent;
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    padding: 0;
  }
  .ms-rail-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    transition: background 120ms ease, transform 120ms ease;
  }
  .ms-rail-btn:hover .ms-rail-icon {
    background: rgba(0,0,0,0.75);
    transform: scale(1.04);
  }
  .ms-rail-btn.is-active .ms-rail-icon {
    background: var(--color-brand-500, #ec4899);
  }
  .ms-rail-icon svg { width: 24px; height: 24px; fill: currentColor; }
  .ms-rail-label {
    font-size: 11px;
    font-weight: 600;
    color: rgba(255,255,255,0.95);
    text-shadow: 0 1px 3px rgba(0,0,0,0.6);
    line-height: 1;
  }

  /* Bottom progress */
  .ms-progress {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 3px;
    background: rgba(255,255,255,0.18);
    z-index: 4;
  }
  .ms-progress-fill {
    height: 100%;
    background: var(--color-brand-500, #ec4899);
    transition: width 80ms linear;
  }
`;
